import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Field, OptionSelect } from "@/components/FormBits";
import { ConfirmAction } from "@/components/ConfirmAction";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/constants";

const ROLES = { operatore: "Operatore", admin: "Amministratore" };
const EMPTY = { name: "", email: "", password: "", role: "operatore", office: "" };

const NewUserDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Utenza creata");
      setForm(EMPTY);
      onOpenChange(false);
      onCreated();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-user-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">Nuova utenza</DialogTitle>
          <DialogDescription>Comunica al collega email e password iniziale.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nome e cognome"><Input data-testid="new-user-name-input" required value={form.name} onChange={set("name")} /></Field>
          <Field label="Email"><Input data-testid="new-user-email-input" type="email" required value={form.email} onChange={set("email")} /></Field>
          <Field label="Password iniziale (min. 6 caratteri)"><Input data-testid="new-user-password-input" required minLength={6} value={form.password} onChange={set("password")} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ruolo"><OptionSelect testId="new-user-role-select" value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={ROLES} /></Field>
            <Field label="Ufficio"><Input data-testid="new-user-office-input" value={form.office} onChange={set("office")} placeholder="Es. Cultura" /></Field>
          </div>
          <Button type="submit" data-testid="new-user-save-button" className="w-full bg-[#8B1E2B] hover:bg-[#701621]">Crea utenza</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PasswordDialog = ({ user, onClose }) => {
  const [pwd, setPwd] = useState("");
  const save = async () => {
    try {
      await api.patch(`/users/${user.id}`, { password: pwd });
      toast.success("Password aggiornata");
      setPwd("");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reset-password-modal">
        <DialogHeader><DialogTitle className="font-serif text-3xl">Reimposta password</DialogTitle><DialogDescription>{user?.name}</DialogDescription></DialogHeader>
        <Input data-testid="reset-password-input" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Nuova password (min. 6)" />
        <Button onClick={save} disabled={pwd.length < 6} data-testid="reset-password-save-button" className="bg-[#1E293B]">Salva</Button>
      </DialogContent>
    </Dialog>
  );
};

export default function Staff() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [pwdUser, setPwdUser] = useState(null);
  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const patch = async (u, data) => {
    try { await api.patch(`/users/${u.id}`, data); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (u) => {
    try { await api.delete(`/users/${u.id}`); toast.success("Utenza eliminata"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="staff-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end gap-4">
        <div>
          <p className="eyebrow">Amministrazione</p>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-semibold text-slate-900">Personale</h1>
          <p className="mt-2 text-slate-600">Gestisci le utenze dei dipendenti che accedono al gestionale.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-user-button" className="sm:ml-auto bg-[#8B1E2B] hover:bg-[#701621] rounded-full"><UserPlus className="h-4 w-4 mr-2" />Nuova utenza</Button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <Table data-testid="staff-table">
          <TableHeader><TableRow className="bg-[#FBFAF7]">{["Dipendente", "Ufficio", "Ruolo", "Ultimo accesso", "Attiva", ""].map((h) => <TableHead key={h} className="eyebrow">{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {users.map((u) => {
              const self = u.id === me.id;
              return (
                <TableRow key={u.id} data-testid={`staff-row-${u.id}`}>
                  <TableCell><div className="font-semibold">{u.name}{self && <span className="ml-2 text-xs text-slate-400">(tu)</span>}</div><div className="text-xs text-slate-500">{u.email}</div></TableCell>
                  <TableCell>{u.office || "—"}</TableCell>
                  <TableCell>{self ? ROLES[u.role] : <OptionSelect testId={`staff-role-${u.id}`} className="w-40" value={u.role} onChange={(v) => patch(u, { role: v })} options={ROLES} />}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtDateTime(u.last_login)}</TableCell>
                  <TableCell><Switch data-testid={`staff-active-${u.id}`} checked={u.active} disabled={self} onCheckedChange={(v) => patch(u, { active: v })} /></TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button variant="ghost" size="icon" onClick={() => setPwdUser(u)} data-testid={`staff-reset-password-${u.id}`}><KeyRound className="h-4 w-4" /></Button>
                    {!self && (
                      <ConfirmAction testId={`staff-delete-${u.id}`} title="Eliminare l'utenza?" description={`${u.name} non potrà più accedere.`} confirmLabel="Elimina" onConfirm={() => remove(u)}
                        trigger={<Button variant="ghost" size="icon" className="text-red-700 hover:bg-red-50" data-testid={`staff-delete-button-${u.id}`}><Trash2 className="h-4 w-4" /></Button>} />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <NewUserDialog open={open} onOpenChange={setOpen} onCreated={load} />
      <PasswordDialog user={pwdUser} onClose={() => setPwdUser(null)} />
    </div>
  );
}
